import * as DateFnsNs from 'date-fns'
import locale from 'date-fns/locale/pt-BR'
import { WaitingScope } from 'src/components/waiting/waiting.scopes'
import { AppStorage, lodash, ServiceError } from 'src/utils'
import {
    AlertSeverity,
    ApplicationPresenter,
    FlipIntent,
    HistoryManager,
    Logger,
    NOOP_VOID,
    Presenter,
    Scope
} from 'src/3rds/cube'
import { Places } from '../Constants'
import { MainKeys } from './Main.keys'
import {
    AlertScope,
    BodyScope,
    ChangePasswordScope,
    IDialogScope,
    MainScope,
    ResetPasswordScope,
    SignInScope
} from './Main.scopes'
import { MainService } from './Main.service'

const LOG = Logger.get('MainPresenter')

const appStorage = AppStorage.singleton()
const mainService = MainService.singleton()

export class MainPresenter extends ApplicationPresenter<MainScope> {
    // :: Instance

    private __removeAccessTokenAboutToExpireListener = NOOP_VOID

    private readonly __signInScope = new SignInScope()
    private readonly __resetPasswordScope = new ResetPasswordScope()
    private readonly __changePasswordScope = new ChangePasswordScope()
    private readonly __defaultScope = new BodyScope()

    private readonly __bodySlot = this.__setBodySlot.bind(this)
    private readonly __dialogSlot = this.showDialog.bind(this)

    private __resetPassword = false
    private __changePassword = false
    private __changePasswordToken?: string
    private __compliance = false
    private __nonAuthenticatedIntent?: FlipIntent
    private __defaultPlace = Places.main

    constructor(historyManager: HistoryManager) {
        super(historyManager, new MainScope())

        // Important to allow newUriFromString to work properly
        this.setPlaces(Places)
    }

    // :: Properties

    get compliance() {
        return this.__compliance
    }

    get language() {
        return 'pt-BR'
    }

    // :: API

    initialize() {
        let initialized = false

        const bootstrap = async () => {
            try {
                await this.kickStart(Places.main)
                initialized = true
            } catch (error) {
                LOG.error('Initializing', error)
                this.release()
            }

            if (initialized && this.lastPlace === Places.main && this.scope.authenticated) {
                try {
                    this.flip(this.__defaultPlace)
                } catch (error) {
                    LOG.error('Flipping to Overview on page initialization', error)
                }
            }
        }

        bootstrap().catch(LOG.caught)

        return () => {
            if (initialized) {
                this.release()
            }
        }
    }

    override release() {
        this.__removeAccessTokenAboutToExpireListener()
        this.__removeAccessTokenAboutToExpireListener = NOOP_VOID
        super.release()
    }

    override async applyParameters(intent: FlipIntent, initialization: boolean, last?: boolean): Promise<boolean> {
        const keys = new MainKeys(this, intent)

        if (initialization) {
            await this.__intializeState(keys)
        } else {
            this.__compliance = keys.compliance
        }
        this.scope.activePage = intent.place.pathName

        if (this.lastPlace !== keys.targetPlace) {
            this.scope.toolbar = undefined
        }

        if (last || !this.scope.authenticated || this.__resetPassword || this.__changePassword) {
            this.scope.toolbar = undefined

            if (intent.place !== Places.main) {
                this.__nonAuthenticatedIntent = intent
            }

            this.__bodySlot(undefined)
            keys.allow = false
        } else {
            keys.parentSlot = this.__bodySlot
            keys.dialogSlot = this.__dialogSlot
        }

        if (keys.last) {
            keys.dialogSlot(null)
        }

        return keys.allow
    }

    override publishParameters(intent: FlipIntent) {
        const keys = new MainKeys(this, intent)
        keys.resetPassword = this.__resetPassword
        keys.changePassword = this.__changePassword
        keys.changePasswordToken = this.__changePasswordToken
        keys.compliance = this.__compliance
    }

    private async __intializeState(keys: MainKeys) {
        this.__resetPassword = keys.resetPassword
        this.__changePassword = keys.changePassword
        this.__compliance = keys.compliance
        this.__changePasswordToken = keys.changePasswordToken

        this.__removeAccessTokenAboutToExpireListener = appStorage.onAccessTokenAboutToExpire(
            this.__handleAccessTokenAboutToExpire
        )

        this.scope.onSignOut = this.action(this._handleSignOut, this)
        this.scope.onProfile = this.action(this._handleProfile, this)
        this.scope.onToggleComplianceMode = this.action(this._handleToggleComplianceMode, this)
        this.scope.onDrawerToggle = this.action(this._handleDrawerToggle, this)
        this.scope.onHome = this.action(this._handleHome, this)
        this.scope.onOverview = this.action(this._handleOverview, this)
        this.scope.onCadastroLote = this.action(this._handleCadastroLote, this)
        this.scope.onCampanha = this.action(this._handleCampanha, this)
        this.scope.onGrupoManual = this.action(this._handleGrupoManual, this)
        this.scope.onDashboards = this.action(this._handleDashboards, this)
        this.scope.onBloqueios = this.action(this._handleBloqueios, this)
        this.scope.onTemplates = this.action(this._handleTemplates, this)
        this.scope.onMensagemDireta = this.action(this._handleMensagemDireta, this)
        this.scope.onSimulador = this.action(this._handleSimulador, this)

        this.__signInScope.rememberMe = appStorage.rememberMe
        this.__signInScope.onSignIn = this.action(this._handleSignIn, this)
        this.__signInScope.onForgotPassword = this.action(this._handleForgotPassword, this)
        this.__signInScope.update = this.update

        this.__resetPasswordScope.onResetPassword = this.action(this._handleResetPassword, this)
        this.__resetPasswordScope.onBackToLogin = this.action(this._handleSignOut, this)
        this.__resetPasswordScope.update = this.update

        this.__changePasswordScope.onChangePassword = this.action(this._handleChangePassword, this)
        this.__changePasswordScope.onBackToLogin = this.action(this._handleSignOut, this)

        this.__defaultScope.update = this.update

        this.scope.authenticated = appStorage.isAuthenticated

        LOG.info(`Initialized(${keys.toString()})`)
    }

    // :: Helper API

    override unexpected(message: string, error: unknown) {
        super.unexpected(message, error)
        if (error instanceof ServiceError) {
            this.alert('error', 'Erro proveniente do servidor', `${error.statusCode}: ${error.message}`)
        } else {
            this.alert('error', 'Erro não esperado', message)
        }
    }

    override alert(severity: AlertSeverity, title: string, message: string, onClose?: () => Promise<void>) {
        const alertScope = new AlertScope()
        alertScope.severity = severity
        alertScope.title = title
        alertScope.message = message
        alertScope.onClose = this.action(() => this._handleCloseAlert(onClose))
        alertScope.update = this.update
        this.scope.alert = alertScope
    }

    notify(severity: AlertSeverity, message: string, duration = 3000) {
        this.scope.notify(severity, message, duration)
    }

    releaseToolbar(toolbar: Presenter<Scope>) {
        if (this.scope.toolbar === toolbar.scope) {
            this.scope.toolbar = undefined
        }
        toolbar.release()
    }

    formatDate(date: Date) {
        return DateFnsNs.format(date, 'dd/MM/yyyy', { locale: locale.ptBR })
    }

    // :: View Actions

    private __setBodySlot(scope: Scope | undefined | null) {
        let newScope = scope
        if (this.__resetPassword) {
            newScope = this.__resetPasswordScope
        } else if (this.__changePassword) {
            newScope = this.__changePasswordScope
        } else if (this.scope.authenticated) {
            newScope = scope ?? this.__defaultScope
        } else {
            newScope = this.__signInScope
        }

        this.scope.body = newScope
    }

    public showDialog(scope: Scope | undefined | null) {
        if (this.scope.dialog !== scope) {
            this.scope.dialog = scope as IDialogScope

            if (this.scope.dialog && !this.scope.dialog.onClose) {
                LOG.error(`Missing onClose action on scope ${this.scope.dialog.constructor.name}`)
            }
        }
    }

    private readonly __handleAccessTokenAboutToExpire = () => {
        LOG.debug('handleAccessTokenAboutToExpire')
        const action = async () => {
            try {
                await mainService.refreshAccessToken(this.__signInScope.rememberMe ?? false)
            } catch (err) {
                this._handleSignOut()
            }
        }
        action().catch(LOG.caught)
    }

    protected async _handleCloseAlert(onClose?: () => Promise<void>) {
        this.scope.alert = undefined
        if (onClose) {
            await onClose()
        }
    }

    protected async _handleLoginSuccess() {
        this.scope.authenticated = true
        appStorage.setAuthenticated(true, this.__signInScope.rememberMe ?? false)

        this.__computeDefaultPlace()

        if (this.__nonAuthenticatedIntent != null) {
            const targetIntent = this.__nonAuthenticatedIntent
            try {
                targetIntent.attributes.clear()

                this.__nonAuthenticatedIntent = undefined
                await this.flipToIntent(targetIntent)
            } catch (err) {
                LOG.error('fliping to intent ' + targetIntent, err)
                await this.flipToDefault()
            }
        } else {
            await this.flipToDefault()
        }
    }

    protected async _handleMicrosoftSignIn(code: string) {
        if (!code) {
            this.__signInScope.errorMessage = 'Usuário desconhecido ou senha incorreta'
            return false
        }

        try {
            await mainService.microsoftSignIn(code)

            await this._handleLoginSuccess()
            return true
        } catch (err) {
            if (err instanceof ServiceError) {
                if (err.statusCode === 401) {
                    if (err.message === 'User is inactive') {
                        this.__signInScope.errorMessage = 'Usuário inativo, contate o administrador do sistema'
                        return false
                    }
                    if (err.message === 'User is deleted') {
                        this.__signInScope.errorMessage = 'Usuário removido, contate o administrador do sistema'
                        return false
                    }
                    this.__signInScope.errorMessage = 'Usuário desconhecido ou senha incorreta'
                    return false
                }
            }
            throw err
        }
    }

    protected async _handleGoogleSignIn(code: string) {
        if (!code) {
            this.__signInScope.errorMessage = 'Usuário desconhecido ou senha incorreta'
            return false
        }

        try {
            await mainService.googleSignIn(code)

            this._handleLoginSuccess()
            return true
        } catch (err) {
            if (err instanceof ServiceError) {
                if (err.statusCode === 401) {
                    if (err.message === 'User is inactive') {
                        this.__signInScope.errorMessage = 'Usuário inativo, contate o administrador do sistema'
                        return false
                    }
                    if (err.message === 'User is deleted') {
                        this.__signInScope.errorMessage = 'Usuário removido, contate o administrador do sistema'
                        return false
                    }
                    this.__signInScope.errorMessage = 'Usuário desconhecido ou senha incorreta'
                    return false
                }
            }
            throw err
        }
    }

    protected async _handleSignIn() {
        LOG.debug('handleSignIn')

        let hasError = false
        this.__signInScope.userNameError = undefined
        this.__signInScope.passwordError = undefined
        this.__signInScope.errorMessage = undefined

        const userName = this.__signInScope.userName
        if (!userName) {
            this.__signInScope.userNameError = 'Campo requirido'
            hasError = true
        }

        const password = this.__signInScope.password
        if (!password) {
            this.__signInScope.passwordError = 'Campo requirido'
            hasError = true
        }

        if (hasError) {
            return
        }

        try {
            await mainService.signIn(userName!, password!, this.__signInScope.rememberMe ?? false)

            this._handleLoginSuccess()
        } catch (err) {
            if (err instanceof ServiceError) {
                if (err.statusCode === 401) {
                    if (err.message === 'User is inactive') {
                        this.__signInScope.errorMessage = 'Usuário inativo, contate o administrador do sistema'
                        return
                    }
                    if (err.message === 'User is deleted') {
                        this.__signInScope.errorMessage = 'Usuário removido, contate o administrador do sistema'
                        return
                    }
                    this.__signInScope.errorMessage = 'Usuário desconhecido ou senha incorreta'
                    return
                }
            }
            throw err
        }
    }

    private __computeDefaultPlace() {
        this.__defaultPlace = Places.overview
    }

    protected async _handleSignOut() {
        LOG.debug('handleSignOut')
        this.scope.authenticated = false
        this.__resetPassword = false
        this.__changePassword = false
        this.__changePasswordToken = undefined
        this.__signInScope.rememberMe = false
        this.__signInScope.userName = undefined
        this.__signInScope.userNameError = undefined
        this.__signInScope.password = undefined
        this.__signInScope.passwordError = undefined
        this.__signInScope.errorMessage = undefined
        appStorage.clearAccessToken()
        await this.flip(this.rootPlace)
    }

    protected async _handleProfile() {
        await this.flip(Places.profile)
    }

    protected async _handleToggleComplianceMode() {
        this.__compliance = !this.__compliance
        this.updateHistory()

        this.forEachPresenter((child) => {
            const childAsRecord = child as unknown as Record<string, unknown>
            const onComplianceChangedListener = childAsRecord['onComplianceChanged']
            if (lodash.isFunction(onComplianceChangedListener)) {
                onComplianceChangedListener.call(child, this.__compliance)
            }
        })
    }

    public async flipToDefault() {
        await this.flip(this.__defaultPlace)
    }

    protected async _handleForgotPassword() {
        LOG.info('handleForgotPassword')
        this.__resetPassword = true
        this.__resetPasswordScope.email = undefined
        await this.flip(this.rootPlace)
    }

    protected async _handleResetPassword() {
        LOG.info('handleResetPassword')
        this.__resetPasswordScope.emailError = undefined
        if (this.__resetPasswordScope.email) {
            await mainService.requestPasswordReset(this.__resetPasswordScope.email)
            this.scope.notify('success', 'Solicitação enviada', 3000)
        } else {
            this.__resetPasswordScope.emailError = 'Campo requerido'
            return
        }

        this.__resetPassword = false
        await this._handleSignOut()
    }

    protected async _handleChangePassword() {
        LOG.info('handleChangePassword')
        this.__changePasswordScope.passwordError = undefined

        if (!this.__changePasswordToken) {
            this.__changePasswordScope.passwordError = 'Token de redefinição não informado'
            return
        }
        if (!this.__changePasswordScope.password) {
            this.__changePasswordScope.passwordError = 'Senha deve ser informada'
            return
        }
        if (!this.__changePasswordScope.passwordConfirmation) {
            this.__changePasswordScope.passwordError = 'Confirmação de senha deve ser informada'
            return
        }

        if (this.__changePasswordScope.password !== this.__changePasswordScope.passwordConfirmation) {
            this.__changePasswordScope.passwordError = 'Senha e Confirmação de senha devem ser iguais'
            return
        }

        try {
            this.__changePasswordScope.waiting = new WaitingScope()

            await mainService.changePassword(
                this.__changePasswordScope.password!,
                this.__changePasswordScope.passwordConfirmation!,
                this.__changePasswordToken!
            )
            this.scope.notify('success', 'Senha alterada com sucesso', 3000)
            this.__changePassword = false
            await this._handleSignOut()
        } catch (error) {
            const err = error as { message: string }
            this.__changePasswordScope.passwordError = err.message
        } finally {
            this.__changePasswordScope.waiting = undefined
        }
    }

    protected async _handleDrawerToggle() {
        this.scope.drawerOpen = !this.scope.drawerOpen
    }

    protected async _handleHome() {
        await this.flip(this.rootPlace)
    }

    protected async _handleOverview() {
        await this.flip(Places.overview)
    }

    protected async _handleCadastroLote() {
        await this.flip(Places.cadastroLote)
    }

    protected async _handleCampanha() {
        await this.flip(Places.campanha)
    }

    protected async _handleGrupoManual() {
        await this.flip(Places.grupoManual)
    }

    protected async _handleDashboards() {
        await this.flip(Places.dashboards)
    }

    protected async _handleBloqueios() {
        await this.flip(Places.bloqueios)
    }

    protected async _handleTemplates() {
        await this.flip(Places.templates)
    }

    protected async _handleMensagemDireta() {
        await this.flip(Places.mensagemDireta)
    }

    protected async _handleSimulador() {
        await this.flip(Places.simulador)
    }
}
